import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Form,
  InputGroup,
  Pagination,
  Modal,
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaClock,
  FaTimesCircle,
  FaUpload,
  FaFilePdf,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import AssignmentService from '../../../../services/assignmentService';
import CreateAssignmentModal from './CreateAssignmentModal';
import EditAssignmentModal from './EditAssignmentModal';
import AssignmentSubmissionsView from './AssignmentSubmissionsView';

const AssignmentsTab = ({ batchId }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    loadAssignments();
  }, [batchId, pagination.current_page, itemsPerPage, searchTerm]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const response = await AssignmentService.getBatchAssignments(
        batchId,
        pagination.current_page,
        itemsPerPage,
        searchTerm
      );
      
      if (response.data && response.data.data) {
        setAssignments(response.data.data);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Failed to load assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAssignment) return;
    try {
      await AssignmentService.deleteAssignment(selectedAssignment.id);
      toast.success('Assignment deleted successfully');
      setShowDeleteModal(false);
      setSelectedAssignment(null);
      loadAssignments();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const handleStatusChange = async (assignment, newStatus) => {
    try {
      await AssignmentService.updateAssignmentStatus(assignment.id, newStatus);
      toast.success(`Assignment ${newStatus === 'published' ? 'published' : 'closed'} successfully`);
      loadAssignments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge bg="success">Published</Badge>;
      case 'draft':
        return <Badge bg="warning">Draft</Badge>;
      case 'closed':
        return <Badge bg="secondary">Closed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />
    );

    if (startPage > 1) {
      items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
      if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === current_page} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1) items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item key={total_pages} onClick={() => handlePageChange(total_pages)}>
          {total_pages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <Form.Select size="sm" value={itemsPerPage} onChange={handleItemsPerPageChange} style={{ width: 'auto' }}>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
          <span className="text-muted small">
            Showing {(pagination.current_page - 1) * itemsPerPage + 1} -{' '}
            {Math.min(pagination.current_page * itemsPerPage, pagination.total)} of {pagination.total} items
          </span>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  if (loading && assignments.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading assignments...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Assignments</h5>
        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
          <FaPlus className="me-1" /> Create Assignment
        </Button>
      </div>

      {/* Search Bar */}
      <InputGroup className="mb-3" style={{ width: '300px' }}>
        <Form.Control
          placeholder="Search assignments..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button variant="outline-primary" onClick={handleSearch}>
          <FaSearch />
        </Button>
        {searchTerm && (
          <Button variant="outline-secondary" onClick={handleClearSearch}>
            Clear
          </Button>
        )}
      </InputGroup>

      {/* Assignments Table */}
      <Table striped hover responsive>
        <thead>
          <tr>
            <th>Title</th>
            <th>Due Date</th>
            <th>Total Marks</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {assignments.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center py-4">
                No assignments created yet.
              </td>
            </tr>
          ) : (
            assignments.map((assignment) => (
              <tr key={assignment.id}>
                <td>
                  <strong>{assignment.title}</strong>
                  {assignment.attachment_name && (
                    <div className="small text-muted">
                      <FaFilePdf className="me-1" /> {assignment.attachment_name}
                    </div>
                  )}
                </td>
                <td className={new Date(assignment.due_date) < new Date() ? 'text-danger' : ''}>
                  <FaClock className="me-1" /> {formatDate(assignment.due_date)}
                </td>
                <td>{assignment.total_marks}</td>
                <td>{getStatusBadge(assignment.status)}</td>
                <td>
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="me-1"
                    onClick={() => {
                      setSelectedAssignment(assignment);
                      setShowSubmissionsModal(true);
                    }}
                    title="View Submissions"
                  >
                    <FaEye />
                  </Button>
                  {assignment.status === 'draft' && (
                    <Button
                      variant="success"
                      size="sm"
                      className="me-1"
                      onClick={() => handleStatusChange(assignment, 'published')}
                      title="Publish"
                    >
                      <FaUpload />
                    </Button>
                  )}
                  {assignment.status === 'published' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleStatusChange(assignment, 'closed')}
                      title="Close"
                    >
                      <FaTimesCircle />
                    </Button>
                  )}
                  <Button
                    variant="warning"
                    size="sm"
                    className="me-1"
                    onClick={() => {
                      setSelectedAssignment(assignment);
                      setShowEditModal(true);
                    }}
                    title="Edit"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setSelectedAssignment(assignment);
                      setShowDeleteModal(true);
                    }}
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                 </td>
               </tr>
            ))
          )}
        </tbody>
      </Table>

      {renderPagination()}

      {/* Modals */}
      <CreateAssignmentModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        batchId={batchId}
        onSuccess={loadAssignments}
      />

      <EditAssignmentModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        onSuccess={loadAssignments}
      />

      <AssignmentSubmissionsView
        show={showSubmissionsModal}
        onHide={() => {
          setShowSubmissionsModal(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
      />

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAssignment && (
            <p>
              Are you sure you want to delete assignment{' '}
              <strong>{selectedAssignment.title}</strong>?
              <br />
              <small className="text-danger">This will also delete all student submissions.</small>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignmentsTab;